import { Skeleton, Stack, Typography } from '@mui/material';

import ChevronRightIcon from '@/components/Icons/ChevronRightIcon';
import { BasetenPageEnum } from '@/routes/types';
import { TableDeploymentStatus } from '@/sections/Workspace/types';
import {
  colorForStatus,
  getEntityDeploymentsCount,
  getVersionsByStatus,
  sortVersionsByStatus,
} from '@/sections/Workspace/utils';

import { useChains } from '../../Chains/hooks';
import { useOrgSharedEndpoints } from '../../ModelAPIs/hooks';
import { useModels } from '../../Models/hooks';
import { useTrainingProjects } from '../../Training/hooks';
import { StatusColor } from '../../styled';
import { WorkspaceSections } from '../../types';
import { AppLink, ListItem } from '../styled';
import { DeploymentsChevronIconContainer, Section, StatusColors } from './styled';

function Deployments() {
  const { trainingProjects, loading: isTrainingProjectsLoading } = useTrainingProjects();
  const { chains, loading: isChainsLoading } = useChains();
  const { models, loading: isModelsLoading } = useModels();
  const { orgSharedEndpoints, loading: isOrgSharedEndpointsLoading } = useOrgSharedEndpoints();

  function getModelsDetails() {
    if (isModelsLoading) {
      return (
        <Stack gap={1} flex={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack gap={1}>
              <Typography variant="h5" color="text.muted">
                Model deployments
              </Typography>
              <Skeleton variant="text" width={100} height={16} />
            </Stack>
            <ChevronRightIcon data-cy="list-item-arrow" />
          </Stack>
          <Skeleton variant="rounded" width="100%" height={6} />
        </Stack>
      );
    }

    const versionsByStatus = models.reduce(
      (acc, model) => getVersionsByStatus(model.versionCountByStatus, acc),
      {} as Record<TableDeploymentStatus, number>,
    );

    const sortedVersionsByStatus = sortVersionsByStatus(versionsByStatus);
    const totalCount = models.reduce((acc, model) => acc + getEntityDeploymentsCount(model), 0);

    const activeCount = versionsByStatus[TableDeploymentStatus.Running];

    const activeCountTitle = models.length > 0 ? `${activeCount}/${totalCount} active` : '0 active';

    return (
      <Stack gap={1} flex={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack gap={1}>
            <Typography variant="h5" color="text.muted">
              Model deployments
            </Typography>
            <Typography variant="h5">{activeCountTitle}</Typography>
          </Stack>
          <DeploymentsChevronIconContainer>
            <ChevronRightIcon />
          </DeploymentsChevronIconContainer>
        </Stack>
        <StatusColors data-cy="status-colors">
          {sortedVersionsByStatus
            .filter(([, count]) => count)
            .map(([status, count]) => (
              <StatusColor
                key={status}
                sx={{
                  width: `${(count / totalCount) * 100}%`,
                  backgroundColor: colorForStatus[status],
                }}
              />
            ))}
        </StatusColors>
      </Stack>
    );
  }

  function getChainsDetails() {
    if (isChainsLoading) {
      return (
        <Stack gap={1} flex={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack gap={1}>
              <Typography variant="h5" color="text.muted">
                Chain deployments
              </Typography>
              <Skeleton variant="text" width={100} height={16} />
            </Stack>
            <DeploymentsChevronIconContainer>
              <ChevronRightIcon />
            </DeploymentsChevronIconContainer>
          </Stack>
          <Skeleton variant="rounded" width="100%" height={6} />
        </Stack>
      );
    }

    const deploymentsByStatus = chains.reduce(
      (acc, chain) => getVersionsByStatus(chain.deploymentCountByStatus, acc),
      {} as Record<TableDeploymentStatus, number>,
    );
    const sortedDeploymentsByStatus = sortVersionsByStatus(deploymentsByStatus);
    const totalCount = chains.reduce((acc, chain) => acc + getEntityDeploymentsCount(chain), 0);
    const activeCount = deploymentsByStatus[TableDeploymentStatus.Running];

    const activeCountTitle = chains.length > 0 ? `${activeCount}/${totalCount} active` : '0 active';

    return (
      <Stack gap={1} flex={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack gap={1}>
            <Typography variant="h5" color="text.muted">
              Chain deployments
            </Typography>
            <Typography variant="h5">{activeCountTitle}</Typography>
          </Stack>
          <DeploymentsChevronIconContainer>
            <ChevronRightIcon />
          </DeploymentsChevronIconContainer>
        </Stack>
        <StatusColors data-cy="status-colors">
          {sortedDeploymentsByStatus
            .filter(([, count]) => count)
            .map(([status, count]) => (
              <StatusColor
                key={status}
                sx={{
                  width: `${(count / totalCount) * 100}%`,
                  backgroundColor: colorForStatus[status],
                }}
              />
            ))}
        </StatusColors>
      </Stack>
    );
  }

  function getTrainingsProjectsDetails() {
    if (isTrainingProjectsLoading) {
      return (
        <Stack gap={1} flex={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack gap={1}>
              <Typography variant="h5" color="text.muted">
                Training projects
              </Typography>
              <Skeleton variant="text" width={100} height={16} />
            </Stack>
            <DeploymentsChevronIconContainer>
              <ChevronRightIcon />
            </DeploymentsChevronIconContainer>
          </Stack>
        </Stack>
      );
    }

    return (
      <Stack gap={1} flex={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack gap={1}>
            <Typography variant="h5" color="text.muted">
              Training projects
            </Typography>
            <Typography variant="h5">{trainingProjects.length}</Typography>
          </Stack>
          <DeploymentsChevronIconContainer>
            <ChevronRightIcon />
          </DeploymentsChevronIconContainer>
        </Stack>
      </Stack>
    );
  }

  return (
    <Section gap={1}>
      <AppLink page={BasetenPageEnum.Workspace} params={{ section: WorkspaceSections.Models }}>
        <ListItem sx={{ p: 2 }}>{getModelsDetails()}</ListItem>
      </AppLink>
      <AppLink page={BasetenPageEnum.Workspace} params={{ section: WorkspaceSections.Chains }}>
        <ListItem sx={{ p: 2 }}>{getChainsDetails()}</ListItem>
      </AppLink>
      <AppLink page={BasetenPageEnum.Workspace} params={{ section: WorkspaceSections.ModelAPIs }}>
        <ListItem sx={{ p: 2 }}>
          <Stack gap={1} flex={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack gap={1}>
                <Typography variant="h5" color="text.muted">
                  Model APIs
                </Typography>
                {isOrgSharedEndpointsLoading ? (
                  <Skeleton variant="text" width={100} height={16} />
                ) : (
                  <Typography variant="h5">{orgSharedEndpoints.length} added</Typography>
                )}
              </Stack>
              <DeploymentsChevronIconContainer>
                <ChevronRightIcon />
              </DeploymentsChevronIconContainer>
            </Stack>
            {isOrgSharedEndpointsLoading ? (
              <Skeleton variant="rounded" width="100%" height={6} />
            ) : (
              <StatusColors data-cy="status-colors">
                <StatusColor
                  sx={{
                    width: '100%',
                    backgroundColor:
                      orgSharedEndpoints.length > 0
                        ? colorForStatus[TableDeploymentStatus.Running]
                        : 'transparent',
                  }}
                />
              </StatusColors>
            )}
          </Stack>
        </ListItem>
      </AppLink>
      <AppLink page={BasetenPageEnum.Workspace} params={{ section: WorkspaceSections.Training }}>
        <ListItem sx={{ p: 2 }}>{getTrainingsProjectsDetails()}</ListItem>
      </AppLink>
    </Section>
  );
}

export default Deployments;
